import { useNotify } from '@/composables/notify'
import outlook from '@/outlook'
import axios from 'axios'

class Api {
  notify = useNotify()
  version = null

  get http () {
    this.version = this.version ?? ((process.env.VUE_APP_MSOPLUGIN_VERSION ?? 'dev') + ' (' + outlook.version() + ')')

    const http = axios.create({
      baseURL: outlook.getSetting('endpoint') + '/api/mso-plugin/v1',
      headers: {
        Authorization: 'Bearer ' + outlook.getSetting('token'),
        'X-App-Version': this.version,
      },
    })
    http.interceptors.response.use((response) => response, (error) => {
      const errorBag = error.response.data.errors
      for (const key in errorBag) {
        for (const message of errorBag[key]) {
          this.notify.error(message)
        }
      }
      if (!errorBag && error.response.data.message) {
        this.notify.error(error.response.data.message)
      }
      return Promise.reject(error)
    })
    return http
  }

  throttle (fn, ttl = 500) {
    const cache = new Map()
    return (...args) => {
      return new Promise((resolve, reject) => {
        const key = JSON.stringify(args)
        const now = new Date().getTime()
        if (cache.has(key) && cache.get(key).expiresAt >= now) {
          cache.get(key).dataPromise.then(resolve, reject)
          return
        }
        const dataPromise = fn.apply(this, args)
        cache.set(key, { expiresAt: now + ttl, dataPromise })
        dataPromise.then(resolve, (error) => {
          cache.delete(key)
          reject(error)
        })
      })
    }
  }

  async initialize () {
    return (await this.http.get('initialize')).data
  }

  getBookables = this.throttle(async function (params) {
    return (await this.http.get('search', { params })).data
  })

  async logout () {
    await this.http.delete('link')
  }

  getServiceProviderItems = this.throttle(async function (serviceProviderId, bookableId = null) {
    return (await this.http.get(`service-provider-items/${serviceProviderId}`, { params: { bookable_id: bookableId } })).data.data
  })

  findEvent = this.throttle(async function ({ from_time: fromTime, to_time: toTime, all_day: allDay, ...params }) {
    const shiftToUTC = (date) => {
      const shifted = new Date(date)
      shifted.setUTCMinutes(shifted.getUTCMinutes() - shifted.getTimezoneOffset())

      return shifted
    }

    return (await this.http.get('event', {
      params: {
        from_time: allDay ? shiftToUTC(fromTime) : fromTime,
        to_time: allDay ? shiftToUTC(toTime) : toTime,
        all_day: allDay,
        ...params,
      },
    })).data.data
  })

  getEvent = this.throttle(async function (eventId) {
    return (await this.http.get(`events/${eventId}`)).data.data
  })

  async createServiceOrder (payload) {
    await this.http.post('service-orders', payload)
  }

  async updateServiceOrder (serviceOrderId, payload) {
    await this.http.patch(`service-orders/${serviceOrderId}`, payload)
  }

  async revokeServiceOrder (serviceOrderId) {
    await this.http.patch(`service-orders/${serviceOrderId}/revoke`)
  }

  async deleteServiceOrder (serviceOrderId) {
    await this.http.delete(`service-orders/${serviceOrderId}`)
  }

  async createVisitorInvitation (payload) {
    await this.http.post('visitor-invitations', payload)
  }

  async checkInVisitorInvitation (visitorInvitationId) {
    await this.http.post(`visitor-invitations/${visitorInvitationId}/check-in`)
  }

  async checkOutVisitorInvitation (visitorInvitationId) {
    await this.http.post(`visitor-invitations/${visitorInvitationId}/check-out`)
  }

  async resendVisitorInvitationEmail (visitorInvitationId) {
    await this.http.post(`visitor-invitations/${visitorInvitationId}/resend-email`)
  }

  async deleteVisitorInvitation (visitorInvitationId) {
    await this.http.delete(`visitor-invitations/${visitorInvitationId}`)
  }

  async getFavourites () {
    return (await this.http.get('favourites')).data
  }

  async addFavourite (payload) {
    await this.http.post('favourites', payload)
  }

  async removeFavourite (payload) {
    await this.http.delete('favourites', { params: payload })
  }
}

const api = new Api()
export default api
